import React, { FC, useState } from "react";
import { IContact } from "./types/Contact";
import { ITag } from "./types/Tag";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useContactsTeamMember } from "./hooks/useContactsTeamMember";
import { useContactColumns } from "./hooks/useContactColumns";
import { MuiServerDataGrid } from "../../components/MuiServerDataGrid/MuiServerDataGrid";
import ContactEmbedded from "./components/ContactEmbaded";
import { useDisclosure } from "@chakra-ui/react";
import UserEmbedded from "./components/UserEmbedded";
import { useCanEnrich } from "hooks/useCanEnrich";
import { Box } from "@mui/material";

interface IContactTableProps {
  contacts: IContact[];
  contactsCount: number;
  isLoadingContacts?: boolean;
  onManageContactTags: (contact: IContact) => void;
  onPaginationChange?: (paginationModel: GridPaginationModel) => void;
  paginationModel: GridPaginationModel;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  teamTags: ITag[];
  teamId: number;
  refetchContactsData: () => void;
  refetchTeamTagsData: () => void;
}

export const ContactsTable: FC<IContactTableProps> = ({
  contacts,
  contactsCount,
  isLoadingContacts = false,
  onManageContactTags,
  onPaginationChange,
  paginationModel,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  teamTags,
  teamId,
  refetchContactsData,
  refetchTeamTagsData,
}) => {
  const { canEnrich } = useCanEnrich();
  const {
    isOpen: isContactDrawerOpen,
    onOpen: openContactDrawer,
    onClose: closeContactDrawer,
  } = useDisclosure();
  const {
    isOpen: isUserDrawerOpen,
    onOpen: openUserDrawer,
    onClose: closeUserDrawer,
  } = useDisclosure();
  const [selectedContact, setSelectedContact] = useState<{
    contactId: string;
    contactType: string;
  } | null>(null);
  const [selectedUser, setSelectedUser] = useState<{
    contactId: string;
    handle: string;
  } | null>(null);

  const handleOpenContactDrawer = (contactId: string, contactType: string) => {
    setSelectedContact({ contactId, contactType });
    openContactDrawer();
  };

  const handleCloseContactDrawer = () => {
    closeContactDrawer();
    // Delay state cleanup and refetch to allow drawer animation to complete
    setTimeout(() => {
      setSelectedContact(null);
      refetchContactsData();
      refetchTeamTagsData();
    }, 500);
  };

  const handleOpenUserDrawer = (contactId: string, handle: string) => {
    setSelectedUser({ contactId, handle });
    openUserDrawer();
  };

  const handleCloseUserDrawer = () => {
    closeUserDrawer();
    // Delay state cleanup and refetch to allow drawer animation to complete
    setTimeout(() => {
      setSelectedUser(null);
      refetchContactsData();
      refetchTeamTagsData();
    }, 500);
  };

  const { teamMembersUser } = useContactsTeamMember(teamId);

  const columns = useContactColumns({
    teamTags,
    onManageContactTags,
    filterModel,
    onFilterModelChange,
    teamMembersUser,
    onOpenContactDrawer: handleOpenContactDrawer,
    onOpenUserDrawer: handleOpenUserDrawer,
    teamId,
    canEnrich,
    refetchContactsData,
  });

  return (
    <>
      <Box sx={{ height: "calc(100vh - 200px)" }} id="contacts-table-container">
        <MuiServerDataGrid<IContact>
          columnVisibilityModel={{
            phone: false,
          }}
          rows={contacts}
          rowCount={contactsCount}
          loading={isLoadingContacts}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationChange={onPaginationChange}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          onRowClick={params => {
            handleOpenContactDrawer(params.row.id, params.row.type);
          }}
          filterDebounceMs={500}
        />
      </Box>
      {selectedContact && (
        <ContactEmbedded
          isOpen={isContactDrawerOpen}
          onClose={handleCloseContactDrawer}
          contactId={selectedContact.contactId}
          contactType={selectedContact.contactType}
          teamId={teamId}
        />
      )}

      {selectedUser && (
        <UserEmbedded
          isOpen={isUserDrawerOpen}
          onClose={handleCloseUserDrawer}
          contactId={selectedUser.contactId}
          teamId={teamId}
          handle={selectedUser.handle}
        />
      )}
    </>
  );
};
